import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Header from "../Components/Header";

// Функция для форматирования текста с Markdown
const formatDescription = (text) => {
  if (!text) return "";
  
  let formatted = text;
  
  // Заменяем символы • на маркеры списка
  formatted = formatted.replace(/^[•●]\s+(.*?)$/gm, '<li class="ml-4 text-white">• $1</li>');
  
  // Заменяем дефисы на маркеры списка
  formatted = formatted.replace(/^[-–]\s+(.*?)$/gm, '<li class="ml-4 text-white">• $1</li>');
  
  // Оборачиваем последовательные li в ul
  formatted = formatted.replace(/(<li.*?>.*?<\/li>\n?)+/g, (match) => {
    return `<ul class="list-none my-2">${match}</ul>`;
  });
  
  // Жирный текст: **текст**
  formatted = formatted.replace(/\*\*(.*?)\*\*/g, '<strong class="font-bold text-white">$1</strong>');
  
  // Курсив: *текст*
  formatted = formatted.replace(/\*(.*?)\*/g, '<em class="italic text-white">$1</em>');
  
  // Заголовки
  formatted = formatted.replace(/^### (.*?)$/gm, '<h3 class="text-lg font-bold text-white mt-3 mb-1">$1</h3>');
  formatted = formatted.replace(/^## (.*?)$/gm, '<h2 class="text-xl font-bold text-white mt-3 mb-1">$1</h2>');
  formatted = formatted.replace(/^# (.*?)$/gm, '<h1 class="text-2xl font-bold text-white mt-3 mb-1">$1</h1>');
  
  // Ссылки: [текст](url)
  formatted = formatted.replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-cyan-400 hover:text-cyan-300 underline">$1</a>');
  
  // Разбиваем на параграфы
  const lines = formatted.split('\n');
  const processedLines = [];
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    
    if (line.includes('<ul') || line.includes('</ul>') || 
        line.includes('<li') || line.includes('<h') || 
        line.includes('<strong') || line.includes('<em') ||
        line.includes('<a')) {
      processedLines.push(line);
    } 
    else if (line.trim() === '') {
      continue;
    }
    else {
      processedLines.push(`<p class="mb-2 last:mb-0 text-white">${line}</p>`);
    }
  }
  
  formatted = processedLines.join('\n');
  
  return formatted;
};

// Компонент для отображения форматированного описания
const FormattedDescription = ({ text }) => {
  if (!text) return null;
  
  const formattedText = formatDescription(text);
  
  return (
    <div 
      className="formatted-content text-sm leading-relaxed space-y-1 text-white"
      dangerouslySetInnerHTML={{ __html: formattedText }}
    />
  );
};

const CourseAudioPlayer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [descExpanded, setDescExpanded] = useState(false);
  
  // Получаем данные из location.state
  const audioData = location.state || {};
  const { 
    title, 
    description, 
    audioUrl, 
    duration: propDuration, 
    fileName, 
    image 
  } = audioData;
  
  const BASE_URL = "https://artdownload2-production.up.railway.app";

  // Telegram WebApp инициализация
  useEffect(() => {
    if (window.Telegram?.WebApp) {
      window.Telegram.WebApp.ready();
      window.Telegram.WebApp.expand();
      
      window.Telegram.WebApp.BackButton.isVisible = true;
      window.Telegram.WebApp.BackButton.onClick(() => {
        console.log('Back button clicked in CourseAudioPlayer');
        navigate(-1);
      });
    }

    return () => {
      if (window.Telegram?.WebApp) {
        window.Telegram.WebApp.BackButton.isVisible = false;
        window.Telegram.WebApp.BackButton.offClick();
      }
    };
  }, [navigate]);

  // Прокрутка в начало при загрузке страницы
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'instant'
    });
    
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);

  return (
    <div className="min-h-screen w-full text-white relative">
      {/* ФОН-КАРТИНКА */}
      <div
        className="fixed inset-0"
        style={{
          backgroundImage: "url('/bghome.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          zIndex: -1
        }}
      />

      {/* ДЕКОР СНИЗУ */}
      <div className="fixed bottom-0 left-0 right-0 h-[150px] bg-[radial-gradient(circle_at_center,rgba(120,0,255,0.15),transparent_70%)] z-0" />

      {/* КОНТЕНТ */}
      <div className="relative z-10 min-h-screen flex flex-col">
        {/* HEADER */}
        <div className="pt-0">
          <Header />
        </div>
        
        {/* ОСНОВНОЙ КОНТЕНТ */}
        <div className="flex-1 px-4 pb-24 overflow-y-auto">
          {/* Кнопка назад */}
          <button
            onClick={() => navigate(-1)}
            className="mb-4 mt-2 p-2 hover:bg-white/10 rounded-lg transition-colors md:block block"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>

          {/* Обложка аудио (если есть) */}
          {image && (
            <div className="mb-6 w-full rounded-2xl overflow-hidden">
              <img 
                src={image.startsWith('/') ? `${BASE_URL}${image}` : image}
                alt={title}
                className="w-full h-48 object-cover rounded-2xl"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.style.display = 'none';
                }}
              />
            </div>
          )}

          {/* Заголовок */}
          {title && (
            <h1 className="text-2xl font-semibold mb-4 text-left break-words whitespace-normal"
                style={{ color: '#C8AFE8' }}>
              {title}
            </h1>
          )}

          {/* Описание с форматированием */}
          {description && (
            <div className="bg-[#241352] border border-white/20 rounded-2xl p-4 mb-6">
              <div
                className={`overflow-hidden transition-all duration-300 ${
                  !descExpanded ? "max-h-48" : "max-h-[1000px]"
                }`}
              >
                <FormattedDescription text={description} />
              </div>

              {description.length > 250 && (
                <div className="flex justify-end mt-2">
                  <button
                    onClick={() => setDescExpanded(prev => !prev)}
                    className="text-sm font-medium hover:opacity-80 transition-opacity"
                    style={{ color: '#34D3FF' }}
                  >
                    {descExpanded ? "Свернуть" : "Читать полностью"}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Аудио файл */}
          <div className="space-y-3">
            <h2 className="text-lg font-medium mb-3" style={{ color: '#C8AFE8' }}>
              Аудио материал
            </h2>
            {audioUrl ? (
              <AudioItem
                title={title || fileName || 'Аудио'}
                url={audioUrl}
                duration={propDuration}
              />
            ) : (
              <div className="bg-[#241352] border border-white/20 rounded-2xl p-6 text-center">
                <p className="text-gray-400">Аудио файл не найден</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Стили для форматированного контента */}
      <style>{`
        .formatted-content {
          word-wrap: break-word;
          line-height: 1.5;
          color: white;
        }
        .formatted-content p {
          margin-bottom: 0.5rem;
          color: white;
        }
        .formatted-content p:last-child {
          margin-bottom: 0;
        }
        .formatted-content ul {
          margin: 0.25rem 0;
          padding-left: 0;
        }
        .formatted-content li {
          margin-bottom: 0.125rem;
          color: white;
          list-style-type: none;
        }
        .formatted-content li:last-child {
          margin-bottom: 0;
        }
        .formatted-content strong {
          font-weight: 700;
          color: white;
        }
        .formatted-content em {
          font-style: italic;
          color: white;
        }
        .formatted-content h1,
        .formatted-content h2,
        .formatted-content h3 {
          margin-top: 0.75rem;
          margin-bottom: 0.25rem;
          font-weight: 600;
          color: white;
        }
        .formatted-content h1:first-child,
        .formatted-content h2:first-child,
        .formatted-content h3:first-child {
          margin-top: 0;
        }
        .formatted-content a {
          color: #34D3FF;
          text-decoration: underline;
          transition: color 0.2s;
        }
        .formatted-content a:hover {
          color: #5FDBFF;
        }
      `}</style>
    </div>
  );
};

const AudioItem = ({ title, url, duration: propDuration }) => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(propDuration || "0:00");

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateProgress = () => {
      if (!audio.duration) return;
      setProgress((audio.currentTime / audio.duration) * 100);
    };

    const onEnded = () => {
      setPlaying(false);
      setProgress(0);
    };

    const onLoadedMetadata = () => {
      // Если длительность не передана из пропсов, вычисляем
      if (!propDuration && audio.duration && !isNaN(audio.duration)) {
        const minutes = Math.floor(audio.duration / 60);
        const seconds = Math.floor(audio.duration % 60);
        setDuration(`${minutes}:${seconds.toString().padStart(2, '0')}`);
      }
    };

    audio.addEventListener("timeupdate", updateProgress);
    audio.addEventListener("ended", onEnded);
    audio.addEventListener("loadedmetadata", onLoadedMetadata);

    // Если длительность уже есть в пропсах, вызываем сразу
    if (propDuration) {
      setDuration(propDuration);
    }

    return () => {
      audio.removeEventListener("timeupdate", updateProgress);
      audio.removeEventListener("ended", onEnded);
      audio.removeEventListener("loadedmetadata", onLoadedMetadata);
    };
  }, [propDuration]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (playing) {
      audio.pause();
      setPlaying(false);
    } else {
      audio.play().catch(err => {
        console.error("Error playing audio:", err);
        setPlaying(false);
      });
      setPlaying(true);
    }
  };

  return (
    <div className="bg-[#241352] border border-white/20 rounded-2xl px-4 py-3 flex items-center gap-3">
      {/* PLAY BUTTON */}
      <button
        onClick={togglePlay}
        className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 hover:opacity-90 transition-opacity"
        style={{ background: '#6B3FD8' }}
      >
        {playing ? (
          <svg width="14" height="14" viewBox="0 0 14 14" fill="white">
            <rect x="1" y="1" width="4" height="12" rx="1" />
            <rect x="9" y="1" width="4" height="12" rx="1" />
          </svg>
        ) : (
          <svg width="14" height="14" viewBox="0 0 14 14" fill="white">
            <path d="M3 2L12 7L3 12V2Z" />
          </svg>
        )}
      </button>

      {/* INFO */}
      <div className="flex-1">
        <div className="text-sm font-medium" style={{ color: '#C8AFE8' }}>{title}</div>

        <div className="h-1 bg-white/20 rounded-full mt-2 overflow-hidden">
          <div
            className="h-full transition-all duration-300"
            style={{ width: `${progress}%`, background: '#34D3FF' }}
          />
        </div>
      </div>

      {/* DURATION */}
      <div className="text-xs opacity-70 min-w-[44px] text-right" style={{ color: '#8D85D5' }}>
        {duration}
      </div>

      <audio ref={audioRef} src={url} preload="metadata" />
    </div>
  );
};

export default CourseAudioPlayer;